/**
 * Recharts Combo implementation — one bar series + one line series on a
 * shared x-axis. Mirrors GoodData "X by Quarter (with cumulative total)"
 * widgets where new-per-period is bars and cumulative is a line overlay.
 */

import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { ComboChartProps, CommonChartProps } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = ComboChartProps & CommonChartProps;

export function RechartsCombo({
  data,
  xKey,
  barKey,
  lineKey,
  barLabel,
  lineLabel,
  dualAxis,
  xLabel,
  yLabel,
}: Props): JSX.Element {
  const theme = chartTheme('recharts');
  const palette = theme.theme.categoricalPalette;
  const barColor = palette[0];
  const lineColor = palette[1];

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.axis.stroke} />
        <XAxis dataKey={xKey} tick={theme.axis.tick} stroke={theme.axis.stroke} label={xLabel} />
        <YAxis yAxisId="left" tick={theme.axis.tick} stroke={theme.axis.stroke} label={yLabel} />
        {dualAxis && (
          <YAxis yAxisId="right" orientation="right" tick={theme.axis.tick} stroke={theme.axis.stroke} />
        )}
        <Tooltip
          content={({ active, payload, label }) =>
            active && payload && payload.length > 0 ? (
              <DrillAwareTooltip
                header={String(label ?? '')}
                rows={payload.map((p, i) => ({
                  color: (p.color as string) ?? (i === 0 ? barColor : lineColor),
                  label: String(p.name ?? p.dataKey ?? ''),
                  value: (p.value as number) ?? 0,
                }))}
              />
            ) : null
          }
        />
        <Legend wrapperStyle={{ fontFamily: theme.legend.fontFamily, fontSize: theme.legend.fontSize }} />
        <Bar
          yAxisId="left"
          dataKey={barKey}
          name={barLabel ?? barKey}
          fill={barColor}
          radius={[4, 4, 0, 0]}
        />
        <Line
          yAxisId={dualAxis ? 'right' : 'left'}
          type="monotone"
          dataKey={lineKey}
          name={lineLabel ?? lineKey}
          stroke={lineColor}
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 4 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
